import type { Color } from "../common/color.js";
import type { DrawableTexture } from "../engine/texture.js";
import {
  compose,
  identity,
  multiply,
  transformPoint,
  invert,
  type Mat2D,
  type Rect2D,
  type Vec2,
} from "./math2d.js";

export interface Node2DOptions {
  name?: string;
  x?: number;
  y?: number;
  /** Radians, clockwise in a y-down space. */
  rotation?: number;
  scaleX?: number;
  scaleY?: number;
  visible?: boolean;
  /** Painted in ascending order within a parent; ties keep insertion order. */
  z?: number;
}

/**
 * A node in the 2D scene graph.
 *
 * Transforms compose down the tree, and the composed matrix is **cached**
 * against a dirty flag that a change to this node or any ancestor clears. That
 * caching is the whole reason this is a class rather than a plain object:
 * recomputing a world matrix per node per frame is a matrix multiply for every
 * node in the scene, every frame, and almost none of them moved.
 */
export class Node2D {
  name: string;
  visible: boolean;
  z: number;

  #x: number;
  #y: number;
  #rotation: number;
  #scaleX: number;
  #scaleY: number;

  #parent: Node2D | null = null;
  #children: Node2D[] = [];
  /** Bumped when a child is added or removed; see `sortedChildren`. */
  #childVersion = 0;
  #sorted: Node2D[] | null = null;
  #sortedVersion = -1;
  /** Each child's `z` when `#sorted` was built, in child order. */
  #sortedZ: number[] = [];

  #local: Mat2D = identity();
  #world: Mat2D = identity();
  #localDirty = true;
  #worldDirty = true;

  constructor(options: Node2DOptions = {}) {
    this.name = options.name ?? "";
    this.#x = options.x ?? 0;
    this.#y = options.y ?? 0;
    this.#rotation = options.rotation ?? 0;
    this.#scaleX = options.scaleX ?? 1;
    this.#scaleY = options.scaleY ?? 1;
    this.visible = options.visible ?? true;
    this.z = options.z ?? 0;
  }

  // --- transform -----------------------------------------------------------

  get x(): number {
    return this.#x;
  }
  set x(v: number) {
    if (this.#x === v) return;
    this.#x = v;
    this.invalidate();
  }

  get y(): number {
    return this.#y;
  }
  set y(v: number) {
    if (this.#y === v) return;
    this.#y = v;
    this.invalidate();
  }

  get rotation(): number {
    return this.#rotation;
  }
  set rotation(v: number) {
    if (this.#rotation === v) return;
    this.#rotation = v;
    this.invalidate();
  }

  get scaleX(): number {
    return this.#scaleX;
  }
  set scaleX(v: number) {
    if (this.#scaleX === v) return;
    this.#scaleX = v;
    this.invalidate();
  }

  get scaleY(): number {
    return this.#scaleY;
  }
  set scaleY(v: number) {
    if (this.#scaleY === v) return;
    this.#scaleY = v;
    this.invalidate();
  }

  get position(): Vec2 {
    return { x: this.#x, y: this.#y };
  }

  setPosition(x: number, y: number): void {
    if (this.#x === x && this.#y === y) return;
    this.#x = x;
    this.#y = y;
    this.invalidate();
  }

  setScale(x: number, y = x): void {
    if (this.#scaleX === x && this.#scaleY === y) return;
    this.#scaleX = x;
    this.#scaleY = y;
    this.invalidate();
  }

  /** This node's transform relative to its parent. */
  get localMatrix(): Mat2D {
    if (this.#localDirty) {
      this.#local = compose(
        { x: this.#x, y: this.#y },
        this.#rotation,
        { x: this.#scaleX, y: this.#scaleY },
      );
      this.#localDirty = false;
    }
    return this.#local;
  }

  /** This node's transform in world space. */
  get worldMatrix(): Mat2D {
    if (this.#worldDirty) {
      const parent = this.#parent;
      this.#world = parent ? multiply(this.localMatrix, parent.worldMatrix) : this.localMatrix;
      this.#worldDirty = false;
    }
    return this.#world;
  }

  /** Where this node sits in world space. */
  get worldPosition(): Vec2 {
    const m = this.worldMatrix;
    return { x: m.tx, y: m.ty };
  }

  /**
   * Mark this node and everything under it as needing recomputation.
   *
   * Downward only, and that is the whole trick: moving a node cannot change
   * where its parent is, so nothing above needs touching, while everything
   * below inherits the change.
   */
  invalidate(): void {
    this.#localDirty = true;
    this.#invalidateWorld();
  }

  #invalidateWorld(): void {
    if (this.#worldDirty) return; // its children are already marked
    this.#worldDirty = true;
    for (const child of this.#children) child.#invalidateWorld();
  }

  toWorld(local: Vec2): Vec2 {
    return transformPoint(this.worldMatrix, local);
  }

  toLocal(world: Vec2): Vec2 {
    return transformPoint(invert(this.worldMatrix), world);
  }

  // --- tree ----------------------------------------------------------------

  get parent(): Node2D | null {
    return this.#parent;
  }

  get children(): readonly Node2D[] {
    return this.#children;
  }

  add<T extends Node2D>(child: T): T {
    if (child === (this as Node2D)) throw new Error("webwin: a node cannot contain itself.");
    if (child.contains(this)) {
      throw new Error("webwin: cannot add an ancestor as a child (would create a cycle).");
    }
    child.#parent?.remove(child);
    child.#parent = this;
    this.#children.push(child);
    this.#childVersion++;
    child.#invalidateWorld();
    return child;
  }

  remove(child: Node2D): boolean {
    const i = this.#children.indexOf(child);
    if (i < 0) return false;
    this.#children.splice(i, 1);
    this.#childVersion++;
    child.#parent = null;
    child.#invalidateWorld();
    return true;
  }

  /** Take this node out of its parent, if it has one. */
  detach(): void {
    this.#parent?.remove(this);
  }

  /** True when `node` is this node or anywhere beneath it. */
  contains(node: Node2D | null): boolean {
    for (let n = node; n; n = n.#parent) if (n === (this as Node2D)) return true;
    return false;
  }

  /** This node and every descendant, parents before children. */
  *walk(): Generator<Node2D> {
    yield this;
    for (const child of this.#children) yield* child.walk();
  }

  /** The first descendant with this name, or null. */
  find(name: string): Node2D | null {
    for (const node of this.walk()) if (node.name === name) return node;
    return null;
  }

  /**
   * Children in paint order: ascending `z`, insertion order breaking ties.
   *
   * Kept between calls. The renderer asks every node for this every frame, and
   * sorting afresh cost three arrays and a wrapper per child each time, leaves
   * included. `z` is a plain field that nothing reports changes to, so the
   * order is checked against the z values it was built from rather than trusted.
   * The array is shared: read it, do not change it.
   */
  sortedChildren(): readonly Node2D[] {
    const children = this.#children;
    const zs = this.#sortedZ;
    if (this.#sorted && this.#sortedVersion === this.#childVersion) {
      let same = true;
      for (let i = 0; i < children.length; i++) {
        if (children[i]!.z !== zs[i]) {
          same = false;
          break;
        }
      }
      if (same) return this.#sorted;
    }
    zs.length = children.length;
    for (let i = 0; i < children.length; i++) zs[i] = children[i]!.z;
    this.#sortedVersion = this.#childVersion;
    // Decorated rather than sorted in place, so equal z keeps insertion order
    // whatever the engine's sort happens to be.
    this.#sorted = children
      .map((node, index) => ({ node, index }))
      .sort((a, b) => a.node.z - b.node.z || a.index - b.index)
      .map((entry) => entry.node);
    return this.#sorted;
  }

  /** Local-space bounds, for culling. Null means "cannot be culled". */
  localBounds(): Rect2D | null {
    return null;
  }

  /**
   * Called once per simulation step, parents before children.
   *
   * Empty here: a plain node is a transform and nothing more. Subclasses and
   * game code override it.
   */
  update(_dt: number): void {}
}

/** How a sprite's own origin sits inside its rectangle. */
export interface Anchor2D {
  x: number;
  y: number;
}

export interface SpriteOptions extends Node2DOptions {
  texture: DrawableTexture;
  /** Sub-rectangle in texture pixels — one frame of a sheet. */
  frame?: Rect2D | null;
  /** Drawn size. Defaults to the frame's size, or the texture's. */
  width?: number;
  height?: number;
  /** 0,0 is top-left; 0.5,0.5 centers it, which is what a rotating thing wants. */
  anchor?: Anchor2D;
  tint?: Color | null;
  opacity?: number;
}

/**
 * A textured quad in the scene.
 *
 * The frame is a sub-rectangle rather than a separate texture so a whole sheet
 * costs one texture bind: the renderer batches by texture, and an animation
 * flipping between frames of one sheet never breaks the batch.
 */
export class Sprite extends Node2D {
  texture: DrawableTexture;
  frame: Rect2D | null;
  width: number;
  height: number;
  anchor: Anchor2D;
  tint: Color | null;
  opacity: number;

  constructor(options: SpriteOptions) {
    super(options);
    this.texture = options.texture;
    this.frame = options.frame ?? null;
    this.width = options.width ?? options.frame?.width ?? options.texture.width;
    this.height = options.height ?? options.frame?.height ?? options.texture.height;
    this.anchor = options.anchor ?? { x: 0.5, y: 0.5 };
    this.tint = options.tint ?? null;
    this.opacity = options.opacity ?? 1;
  }

  override localBounds(): Rect2D {
    return {
      x: -this.anchor.x * this.width,
      y: -this.anchor.y * this.height,
      width: this.width,
      height: this.height,
    };
  }
}
